import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getAccountMasters, getAccountHeads, saveAccountHead, getNextAccountHeadCode } from "../../components/Api/GmsFinance";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";
import {
  hasPagePermissionWithAdmin,
  hasDeletePermissionWithAdmin,
  hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";

const initialForm = {
  accountHeadID: 0,
  accountHeadNameEn: "",
  accountHeadNameAr: "",
  accountMasterID: "",
  status: true
};

const AccountHeadPage = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [heads, setHeads] = useState([]);
  const [masters, setMasters] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ accountMasterID: "" });
  const [nextCode, setNextCode] = useState("");

  // Permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const pageRoute = '/GmsAccountHead';
  const hasView = hasPagePermissionWithAdmin(permissions, pageRoute);
  const hasEdit = hasEditOrHigherPermission(permissions, pageRoute);

  useEffect(() => {
    if (!hasView) {
      window.location.href = "/Dashboard";
    } else {
      loadData();
    }
  }, [hasView]);

  useEffect(() => {
    if (formData.accountMasterID && !formData.accountHeadID) {
      setNextCode("");
      getNextAccountHeadCode(formData.accountMasterID)
        .then(res => setNextCode(res.code))
        .catch(err => console.error("Error fetching next code", err));
    } else {
      setNextCode("");
    }
  }, [formData.accountMasterID, formData.accountHeadID]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mastersData, headsData] = await Promise.all([
        getAccountMasters(),
        getAccountHeads()
      ]);
      setMasters(mastersData);
      setHeads(headsData);
    } catch (error) {
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredHeads = useMemo(() => {
    return heads.filter(h => {
      return !filters.accountMasterID || h.accountMasterID === parseInt(filters.accountMasterID);
    });
  }, [heads, filters]);

  const handleEdit = (head) => {
    setFormData({
      accountHeadID: head.accountHeadID,
      accountHeadNameEn: head.accountHeadNameEn,
      accountHeadNameAr: head.accountHeadNameAr || "",
      accountMasterID: head.accountMasterID,
      status: head.status
    });
    setActiveTab("1"); // Move to Add/Edit tab
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.accountMasterID || !formData.accountHeadNameEn) {
      Swal.fire("Warning", "Please fill required fields", "warning");
      return;
    }

    setSaving(true);
    try {
      const response = await saveAccountHead(formData);
      // Response now returns { id, msg, success }
      if (response.success) {
        Swal.fire("Success", response.msg, "success");
        setFormData(initialForm);
        loadData();
        // setActiveTab("2"); // Stay on the current tab as requested
      } else {
        Swal.fire("Error", response.msg || "Failed to save", "error");
      }
    } catch (error) {
      console.error("Save Error:", error);
      Swal.fire("Error", "Server error", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Finance & Accounts" breadcrumbItem="Account Head" />

        <Row>
          <Col lg={12}>
            <Card className="shadow-sm border-0" style={{ borderRadius: '15px' }}>
              <CardBody>
                <div className="wizard clearfix">
                  <div className="steps clearfix">
                    <ul>
                      {hasEdit && (
                        <NavItem className={classnames({ current: activeTab === "1" })}>
                          <NavLink
                            className={classnames({ active: activeTab === "1" })}
                            onClick={() => {
                                if (activeTab === "2") setFormData(initialForm);
                                setActiveTab("1");
                            }}
                          >
                            <span className="number">1.</span> {formData.accountHeadID ? "Edit Account Head" : "Add Account Head"}
                          </NavLink>
                        </NavItem>
                      )}
                      <NavItem className={classnames({ current: activeTab === "2" })}>
                        <NavLink
                          className={classnames({ active: activeTab === "2" })}
                          onClick={() => setActiveTab("2")}
                        >
                          <span className="number">2.</span> Account Head List
                        </NavLink>
                      </NavItem>
                    </ul>
                  </div>

                  <div className="content clearfix mt-4">
                    <TabContent activeTab={activeTab}>
                      <TabPane tabId="1">
                        {hasEdit ? (
                           <div className="p-3">
                             <h5 className="mb-4 text-primary">{formData.accountHeadID ? "Update Existing Head" : "Add New Account Head"}</h5>
                             <Form onSubmit={handleSubmit}>
                               <Row>
                                 <Col md={6}>
                                   <div className="mb-4">
                                     <Label className="fw-bold">Account Master <span className="text-danger">*</span></Label>
                                     <select
                                       className="form-select"
                                       value={formData.accountMasterID}
                                       onChange={(e) => setFormData({ ...formData, accountMasterID: e.target.value })}
                                       disabled={formData.accountHeadID !== 0}
                                       required
                                     >
                                       <option value="">Select Master Category</option>
                                       {masters.map(m => (
                                         <option key={m.accountMasterID} value={m.accountMasterID}>
                                           {m.accountMasterNameEn}
                                         </option>
                                       ))}
                                     </select>
                                   </div>
                                 </Col>
                                 {formData.accountMasterID ? (
                                   <Col md={6}>
                                     <div className="mb-4">
                                       <Label className="fw-bold">Head Code</Label>
                                       <Input
                                         type="text"
                                         className="bg-light"
                                         value={formData.accountHeadID !== 0 
                                           ? (heads.find(h => h.accountHeadID === formData.accountHeadID)?.accountHeadCode || "") 
                                           : (nextCode || "Fetching...")}
                                         readOnly
                                       />
                                     </div>
                                   </Col>
                                 ) : null}
                                 <Col md={6}>
                                   <div className="mb-4">
                                     <Label className="fw-bold">Head Name (English) <span className="text-danger">*</span></Label>
                                     <Input
                                       type="text"
                                       placeholder="Enter Head Name in English"
                                       value={formData.accountHeadNameEn}
                                       onChange={(e) => setFormData({ ...formData, accountHeadNameEn: e.target.value })}
                                       required
                                     />
                                   </div>
                                 </Col>
                                 <Col md={6}>
                                   <div className="mb-4">
                                     <Label className="fw-bold">Head Name (Arabic)</Label>
                                     <Input
                                       type="text"
                                       placeholder="Enter Head Name in Arabic"
                                       value={formData.accountHeadNameAr}
                                       onChange={(e) => setFormData({ ...formData, accountHeadNameAr: e.target.value })}
                                     />
                                   </div>
                                 </Col>
                                 <Col md={6}>
                                   <div className="mb-4 d-flex align-items-center h-100 mt-2">
                                     <div className="form-check form-switch form-switch-md">
                                       <Input
                                         type="checkbox"
                                         className="form-check-input"
                                         id="statusSwitch"
                                         checked={formData.status}
                                         onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                                       />
                                       <Label className="form-check-label ms-2" htmlFor="statusSwitch">Active Status</Label>
                                     </div>
                                   </div>
                                 </Col>
                               </Row>

                               <div className="mt-4 d-flex gap-2">
                                 <Button color="primary" type="submit" disabled={saving}>
                                   {saving && <ClipLoader size={15} color="#fff" className="me-1" />}
                                   {formData.accountHeadID ? "Update Account Head" : "Save Account Head"}
                                 </Button>
                                 <Button color="light" type="button" onClick={() => { setFormData(initialForm); setActiveTab("2"); }}>
                                   Cancel
                                 </Button>
                               </div>
                             </Form>
                           </div>
                        ) : (
                          <div className="alert alert-warning">You do not have permission to add or edit.</div>
                        )}
                      </TabPane>

                      <TabPane tabId="2">
                        <div className="p-2">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h5 className="text-primary mb-0">All Account Heads</h5>
                            <Button
                              color="soft-primary"
                              onClick={() => setShowFilters(!showFilters)}
                              className="d-flex align-items-center gap-2"
                            >
                              <i className={`bx ${showFilters ? 'bx-chevron-up' : 'bx-filter'} font-size-18`}></i>
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>

                          {showFilters && (
                            <Card className="bg-light border-0 mb-4" style={{ borderRadius: '12px' }}>
                              <CardBody className="p-3">
                                <Row className="align-items-end">
                                  <Col md={4}>
                                    <Label className="fw-bold font-size-13 text-muted">Filter by Account Master</Label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={filters.accountMasterID}
                                      onChange={(e) => setFilters({ ...filters, accountMasterID: e.target.value })}
                                    >
                                      <option value="">All Categories</option>
                                      {masters.map(m => (
                                        <option key={m.accountMasterID} value={m.accountMasterID}>
                                          {m.accountMasterNameEn}
                                        </option>
                                      ))}
                                    </select>
                                  </Col>
                                  <Col md={2}>
                                    <Button 
                                      color="light" 
                                      size="sm" 
                                      block 
                                      onClick={() => setFilters({ accountMasterID: "" })}
                                    >
                                      Reset
                                    </Button>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          )}

                          {loading ? (
                            <div className="text-center py-5">
                              <ClipLoader color="#556ee6" size={50} />
                              <p className="mt-3 text-muted">Fetching records...</p>
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <Table className="table table-bordered table-hover align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: "80px" }}>Sr.No</th>
                                    <th>Master Code</th>
                                    <th>Master Category</th>
                                    <th>Head Code</th>
                                    <th>Head Name (EN)</th>
                                    <th>Head Name (AR)</th>
                                    <th>Status</th>
                                    {hasEdit && <th className="text-center">Action</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredHeads.length > 0 ? (
                                    filteredHeads.map((head, idx) => (
                                      <tr key={idx}>
                                        <td>{idx + 1}</td>
                                        <td><Badge color="soft-info" className="font-size-12">{head.accountMasterCode}</Badge></td>
                                        <td>{head.accountMasterNameEn}</td>
                                        <td><span className="fw-bold text-primary">{head.accountHeadCode}</span></td>
                                        <td>{head.accountHeadNameEn}</td>
                                        <td>{head.accountHeadNameAr || "-"}</td>
                                        <td>
                                          <Badge color={head.status ? "success" : "danger"} className="p-2">
                                            {head.status ? "Active" : "Inactive"}
                                          </Badge>
                                        </td>
                                        {hasEdit && (
                                          <td className="text-center">
                                            <Button
                                              size="sm"
                                              color="soft-primary"
                                              className="me-2"
                                              onClick={() => handleEdit(head)}
                                            >
                                              <i className="bx bxs-edit font-size-16"></i> Edit
                                            </Button>
                                          </td>
                                        )}
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="8" className="text-center py-4 text-muted">No records found</td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          )}
                        </div>
                      </TabPane>
                    </TabContent>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccountHeadPage;
